import { useCallback } from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { selectors, actionCreators } from '../../../redux/ducks/uploads';
import { actionCreators as nodeActionCreators } from '../../../redux/ducks/resource-nodes';

import { ResourceIdentifier } from '@cube3/common/model/resource-types';
import { Upload } from '@cube3/common/model/schema/resources/file-upload';
import { useTypedSelector } from './useTypedSelector';
import { useRef } from 'react';
import { useCurrentWorkspace } from '../Administration/withCurrentWorkspace';
import { uuidv4 } from '../../../utils/uuid';
import { NestedMetadata } from '../../../wrapped-cube-client/uploader';

interface Config {
  /** predefined parent resource to upload to*/
  parentResource?: ResourceIdentifier;
  /** predefined parent Folder to upload to*/
  parentFolderId?: string;
}

interface Response {
  /** all uploads created during current app session (since last page load) */
  allUploads: Upload[];
  /** all uploads for folder suplied in config */
  uploadsForFolder?: Upload[];
  /** all uploads for parent resource suplied in config */
  uploadsForParentResource?: Upload[];
  /** uploads files the parent folder suplied through config or second argument */
  uploadFilesToFolder(
    files: any,
    parentFolderId?: string,
    useTree?: boolean,
    tusMetadata?: NestedMetadata
  ): void;
  /** uploads files the parent resource suplied through config or second argument */
  uploadFilesToParentResource(
    files: any,
    parentResource?: ResourceIdentifier,
    tusMetadata?: NestedMetadata
  ): void;
  /** clears queued uploads parent resource suplied through config or first argument */
  clearParentResourceUploads(parentResource?: ResourceIdentifier): void;
  /** removes a specific queued upload suplied through the first argument */
  removeUpload(upload: Upload);
}

export const useUploads = ({
  parentResource,
  parentFolderId
}: Config = {}): Response => {
  const [workspaceId] = useCurrentWorkspace();
  const allUploads = useTypedSelector((state) => {
    return selectors.getUploadsByWorkspace(state, workspaceId) || [];
  });

  const allUploadsRef = useRef(allUploads);
  allUploadsRef.current = allUploads;

  const uploadsForFolder = useTypedSelector((state) => {
    return parentFolderId
      ? selectors.getUploadsByFolder(state, parentFolderId)
      : undefined;
  });
  const uploadsForParentResource = useTypedSelector((state) => {
    return parentResource
      ? selectors.getUploadsByParentResource(state, parentResource)
      : undefined;
  });

  const parentResourceId = parentResource?.id;
  const parentResourceType = parentResource?.type;

  const dispatch = useDispatch();

  const uploadFilesToFolder = useCallback(
    (
      files,
      parentFolderIdDynamic: string = undefined,
      useTree = false,
      tusMetadata
    ) => {
      if (!parentFolderIdDynamic && !parentFolderId) {
        console.warn(
          `Missing parentResource parameter for "uploadFilesToFolder"`
        );
        return;
      }
      if (!useTree) {
        return dispatch(
          actionCreators.uploadFilesToFolder(
            files,
            parentFolderIdDynamic || parentFolderId,
            tusMetadata
          )
        );
      } else {
        const tree = getFolderStructure(files);
        return dispatch(
          actionCreators.uploadWithFolderStructure(
            tree,
            parentFolderIdDynamic || parentFolderId,
            tusMetadata
          )
        );
      }
    },
    [dispatch, parentFolderId]
  );
  const uploadFilesToParentResource = useCallback(
    (
      files,
      parentResourceDynamic: ResourceIdentifier = undefined,
      tusMetadata
    ) => {
      if (!parentResourceDynamic && !(parentResourceId && parentResourceType)) {
        console.warn(
          `Missing parentResource parameter for "uploadFilesToParentResource"`
        );
        return;
      }
      return dispatch(
        actionCreators.uploadFilesToParentResource(
          files,
          parentResourceDynamic || {
            id: parentResourceId,
            type: parentResourceType
          },
          tusMetadata
        )
      );
    },
    [dispatch, parentResourceId, parentResourceType]
  );
  const clearParentResourceUploads = useCallback(
    (parentResourceDynamic: ResourceIdentifier = undefined) => {
      if (!parentResourceDynamic && !(parentResourceId && parentResourceType)) {
        console.warn(
          `Missing parentResource parameter for "clearParentResourceUploads"`
        );
        return;
      }
      return dispatch(
        actionCreators.clearParentResourceUploads({
          id: parentResourceId,
          type: parentResourceType
        })
      );
    },
    [dispatch, parentResourceId, parentResourceType]
  );

  const removeUpload = useCallback(
    (upload) => {
      return dispatch(
        nodeActionCreators.disposeResource('file-upload', upload.id, true)
      );
    },
    [dispatch]
  );

  const response = useMemo(() => {
    return {
      allUploads,
      uploadsForFolder,
      uploadsForParentResource,
      uploadFilesToFolder,
      uploadFilesToParentResource,
      clearParentResourceUploads,
      removeUpload
    };
  }, [
    allUploads,
    uploadsForFolder,
    uploadsForParentResource,
    uploadFilesToFolder,
    uploadFilesToParentResource,
    clearParentResourceUploads,
    removeUpload
  ]);

  return response;
};

// TODO: verify this works on windows
const slashExp = /\//g;
// react-dropzone adds paths to the file objects
// we can infer the folder structure from that.
// NOTE: empty folders don't have file entries, so they get ignored
const getFolderStructure = (files) => {
  // We build a tree structure based on presented file paths, so we
  // can deduplicate paths. Every unique folder also gets added to a
  // flat list, that's easier to use in other steps (create folder API reqs).
  const tree = files.reduce(
    (acc, val) => {
      // split the path into segments representing folders and finaly filename
      const folders = val.path.split(slashExp).filter((f) => !!f);
      // the current working dir is a pointer in the
      // tree. We start walking at the root
      let cwd = acc;
      folders.forEach((f, idx) => {
        if (idx === folders.length - 1) {
          // if this is the last path segment, it is the filename, so we push a file entry
          cwd.files.push(val);
        } else {
          // for the folder segments we check if that folder is already in the tree, and otherwise add it
          const existing = cwd.folders[f];
          if (!existing) {
            cwd.folders[f] = {
              folders: {},
              files: [],
              parent: cwd,
              name: f,
              depth: idx,
              tempId: uuidv4()
            };
            // also push into the flat list
            acc.allFolders.push(cwd.folders[f]);
          }
          cwd = cwd.folders[f];
        }
      });
      return acc;
    },
    // initialize with the root folder
    {
      folders: {},
      files: [],
      allFolders: [],
      name: '/',
      tempId: uuidv4()
    }
  );
  return tree;
};
